import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCard, SpecraCardContent } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { cn } from '../../../lib/utils';
import { Choice, TextField } from '../PastPerformance/Fields';
import { FrozenOutputContext } from './OutputContext';
import { accessDenied } from './outputPresentationModel';
import { SourceContent } from './SourceContent';
const ids = Testids.captureOutputPresentation;
export function OutputPresentation({ captureProjectId, version, query, queryKey, canWrite, onDenied }) {
    const cache = useQueryClient();
    const [theme, setTheme] = useState('standard');
    const [layout, setLayout] = useState('detailed');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [messageIsError, setMessageIsError] = useState(false);
    const inFlight = useRef(false);
    const mounted = useRef(true);
    useEffect(()=>{
        mounted.current = true;
        return ()=>{
            mounted.current = false;
        };
    }, []);
    const settings = query.data?.presentation;
    useEffect(()=>{
        if (settings) {
            setTheme(settings.theme);
            setLayout(settings.layout);
        }
    }, [
        settings
    ]);
    const revision = settings?.revision ?? 0;
    const actions = query.data?.allowedActions ?? [];
    const decisions = (query.data?.dispositions ?? []).filter((d)=>d.presentationRevision === revision);
    const exportState = decisions.some((d)=>d.action === 'authorize_export') ? 'authorized' : decisions.some((d)=>d.action === 'internal_release') ? 'internal' : '';
    const dirty = theme !== settings?.theme || layout !== settings?.layout;
    async function act(action) {
        if (inFlight.current || !settings) return;
        inFlight.current = true;
        setBusy(true);
        setMessage('');
        setMessageIsError(false);
        const basis = {
            captureProjectId,
            outputVersionId: version.id,
            presentationRevision: revision
        };
        try {
            if (action === 'save') {
                await projectDataClient.saveCaptureOutputPresentation({
                    captureProjectId,
                    outputVersionId: version.id,
                    expectedRevision: revision,
                    theme,
                    layout
                });
                if (!mounted.current) return;
                await query.refetch({
                    throwOnError: true
                });
                setMessage('Presentation saved. Source facts and version history are unchanged.');
            } else if (action === 'export') {
                const result = await projectDataClient.exportCaptureOutput({
                    ...basis,
                    format: 'pptx'
                });
                if (!mounted.current) return;
                const blob = new Blob([
                    new Uint8Array(result.content)
                ], {
                    type: result.mimeType
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = result.filename;
                link.click();
                URL.revokeObjectURL(url);
                setMessage('Presentation downloaded.');
            } else {
                await projectDataClient.recordCaptureOutputDisposition({
                    ...basis,
                    action,
                    reason: action === 'authorize_export' ? reason.trim() : 'Internal review only'
                });
                if (!mounted.current) return;
                await query.refetch({
                    throwOnError: true
                });
                setMessage(action === 'authorize_export' ? 'External export authorized for this version and presentation revision.' : 'Shared for internal review. Separate external export authorization required.');
            }
        } catch (error) {
            if (!mounted.current) return;
            if (accessDenied(error)) {
                cache.removeQueries({
                    queryKey
                });
                onDenied();
            } else {
                setMessageIsError(true);
                setMessage(ConnectError.from(error).rawMessage);
            }
        } finally{
            inFlight.current = false;
            if (mounted.current) setBusy(false);
        }
    }
    if (query.isPending || !settings && !query.isError) return /*#__PURE__*/ _jsx("output", {
        "data-testid": ids.loading,
        children: "Loading presentation…"
    });
    if (query.isError || !settings) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            "Presentation could not be loaded.",
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>query.refetch(),
                children: "Retry presentation"
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": ids.root,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    children: "Presentation"
                }),
                busy && /*#__PURE__*/ _jsx("output", {
                    "data-testid": ids.loading,
                    "aria-live": "polite",
                    children: "Saving / preparing presentation…"
                }),
                /*#__PURE__*/ _jsxs("output", {
                    "data-testid": ids.status,
                    "data-revision": revision,
                    "data-export-state": exportState,
                    children: [
                        "Revision ",
                        revision,
                        " ·",
                        ' ',
                        exportState === 'authorized' ? 'External export authorized' : exportState === 'internal' ? 'Internal sharing only' : 'Not shared yet'
                    ]
                }),
                /*#__PURE__*/ _jsxs("fieldset", {
                    disabled: busy || !canWrite || !actions.includes('update'),
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(Choice, {
                            label: "Theme",
                            testId: ids.theme,
                            value: theme,
                            onChange: setTheme,
                            options: [
                                {
                                    value: 'standard',
                                    label: 'Standard'
                                },
                                {
                                    value: 'executive',
                                    label: 'Executive'
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Choice, {
                            label: "Layout",
                            testId: ids.layout,
                            value: layout,
                            onChange: setLayout,
                            options: [
                                {
                                    value: 'detailed',
                                    label: 'Detailed'
                                },
                                {
                                    value: 'compact',
                                    label: 'Compact'
                                }
                            ]
                        })
                    ]
                }),
                canWrite && actions.includes('update') && /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.save,
                    disabled: busy || !dirty,
                    onClick: ()=>act('save'),
                    children: "Save presentation"
                }),
                version.externalFile ? /*#__PURE__*/ _jsx("p", {
                    children: "The uploaded final file has no structured preview or PPTX rendering. Its retained basis is shown below."
                }) : /*#__PURE__*/ _jsxs("div", {
                    "data-testid": ids.preview,
                    "data-version-id": version.id,
                    className: cn('grid grid-cols-1 gap-4 rounded-md p-4', theme === 'executive' ? 'bg-accent text-accent-foreground' : 'bg-card text-card-foreground', layout === 'compact' && 'sm:grid-cols-2'),
                    children: [
                        /*#__PURE__*/ _jsx(FrozenOutputContext, {
                            context: version.projection?.context
                        }),
                        version.projection?.sections.filter((s)=>s.key !== 'output_context').map((section)=>{
                            const override = version.overrides.find((o)=>o.sectionKey === section.key);
                            return /*#__PURE__*/ _jsxs("section", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("h5", {
                                        children: section.title
                                    }),
                                    override ? /*#__PURE__*/ _jsx("p", {
                                        className: "whitespace-pre-wrap",
                                        children: override.wording
                                    }) : section.sources.map((s)=>/*#__PURE__*/ _jsx(SourceContent, {
                                            content: s.contentJson
                                        }, `${s.id}:${s.version}`)),
                                    /*#__PURE__*/ _jsx("p", {
                                        children: section.reason
                                    })
                                ]
                            }, section.key);
                        })
                    ]
                }),
                actions.includes('release') && /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.internalRelease,
                    disabled: busy || dirty,
                    onClick: ()=>act('internal_release'),
                    children: "Share for internal review"
                }),
                actions.includes('export') && !version.externalFile && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "External export purpose",
                            testId: ids.exportReason,
                            value: reason,
                            onChange: setReason
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.authorizeExport,
                            disabled: busy || dirty || !reason.trim(),
                            onClick: ()=>act('authorize_export'),
                            children: "Authorize external export"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.export,
                            disabled: busy || dirty,
                            onClick: ()=>act('export'),
                            children: "Export PPTX"
                        })
                    ]
                }),
                message && /*#__PURE__*/ _jsx("div", {
                    "data-testid": ids.message,
                    role: messageIsError ? 'alert' : 'status',
                    "aria-live": messageIsError ? 'assertive' : 'polite',
                    children: message
                })
            ]
        })
    });
}
